import { useEffect, useMemo } from 'react'
import { FormProvider, useFieldArray, useForm, type Resolver } from 'react-hook-form'
import { CalculatorIcon } from '@phosphor-icons/react'
import { Box, useMediaQuery } from '@mui/material'

import { BaseModal2 } from '@components/modals/BaseModal2'
import { emotionTheme } from '@styles/theme'
import { initialFormValues } from './form/initialFormValues'
import { Input } from '@components/shared/Input'
import { adjustPreviewModalSchema } from './form/validations'
import { RowBox } from '@components/shared/RowBox'
import { setInitialValues } from './form/setInitialValues'
import { Select } from '@components/shared/Select'

import type { SeeAdjustPreviewModalProps } from './types'
import type { AdjustPreviewFormProps } from './form/types'

import * as S from './styles'

export const SeeAdjustPreviewModal = ({
  open,
  onClose,
  periodSelectedData,
  adjustPreviewListData,
  adjustPreviewListLoading,
  consolidatedPreviewFiltersData,
  userData,
  ...rest
}: SeeAdjustPreviewModalProps) => {
  const isMobile = useMediaQuery(`(max-width: ${emotionTheme.screenSizes.laptop})`)

  const methods = useForm<AdjustPreviewFormProps>({
    defaultValues: initialFormValues,
    resolver: adjustPreviewModalSchema as unknown as Resolver<AdjustPreviewFormProps>
  })

  const { fields } = useFieldArray({
    control: methods.control,
    name: 'accounts'
  })

  const accountOptions = useMemo(() => {
    const options = consolidatedPreviewFiltersData?.account?.length
      ? consolidatedPreviewFiltersData.account
        .filter((account) => account.code.toUpperCase() !== 'ALL')
        .map((account) => ({ label: account.name, value: account.code }))
      : []

    adjustPreviewListData?.forEach((adjust) => {
      const alreadyInList = options.some((option) => option.label === adjust.accountName)

      if (!alreadyInList) {
        options.push({ label: adjust.accountName, value: adjust.accountName })
      }
    })

    return options
  }, [consolidatedPreviewFiltersData, adjustPreviewListData])

  useEffect(() => {
    if (open) {
      methods.reset(setInitialValues(periodSelectedData, adjustPreviewListData, consolidatedPreviewFiltersData?.account))
    }
  }, [periodSelectedData, adjustPreviewListData, consolidatedPreviewFiltersData, open])

  useEffect(() => {
    !open && methods.reset(initialFormValues)
  }, [open])

  return (
    <>
      <BaseModal2
        open={open}
        title="Ver Ajustes"
        headerIcon={<CalculatorIcon />}
        onClose={onClose}
        width={isMobile ? 'calc(100% - 20px)' : 870}
        footer={{
          cancelButtonText: 'Voltar',
        }}
        loading={adjustPreviewListLoading}
        {...rest}
      >
        <FormProvider {...methods}>
          <S.Form>
            <RowBox columns={4}>
              <Box sx={{ gridColumn: 'span 2' }}>
                <Input name="period" label="Período Competência" required disabled />
              </Box>
              <Input name="dateCompetence" label="Data Competência" required disabled />
              <Input name="week" label="Semana" disabled />
            </RowBox>

            <S.AdjustmentsList $hasScroll={fields.length > 3}>
              {fields.map((field, index) => (
                <S.InputsBox key={field.id}>
                  <RowBox columns={5}>
                    <Input
                      name={`accounts[${index}].week`}
                      label="Semana"
                      disabled
                    />
                    <Select
                      required
                      label="Conta"
                      name={`accounts.${index}.account`}
                      options={accountOptions}
                      disabled
                    />
                    <Input
                      name={`accounts[${index}].adjustmentValue`}
                      label="Valor Ajuste (R$)"
                      placeholder="R$ 0,00"
                      maskType="currency"
                      disabled
                    />

                    <Box sx={{ gridColumn: 'span 2' }}>
                      <Input
                        required
                        name={`accounts[${index}].observations`}
                        label="Observações"
                        disabled
                      />
                    </Box>
                  </RowBox>
                </S.InputsBox>
              ))}
            </S.AdjustmentsList>
          </S.Form>
        </FormProvider>

        <S.Observation>{`Observação: Ajustado por ${userData?.email}`}</S.Observation>
      </BaseModal2>
    </>
  )
}
